import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { EditOtherForm } from 'components/EditOtherForm';
import { EditOtherFormNew } from 'components/EditOtherForm/EditOtherFormNew';
import { useEditorParseData } from 'components/FormulaEditor';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimContractOtherCostData, ConstructionTypeData, ContractOtherCostUpdateData } from 'gql/graphql';
import {
    ContractOtherStatusEnum,
    OtherCostCalculationTypeEnum,
    ProjectMethodologyEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { useProjectData } from 'hooks/useProjectData';
import { isNotNil } from 'utils';
import { replacerCost } from 'utils/replacerCost';

import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';

import { useUerContractOtherCostLimitSpanSelectionList } from '../../../UerProject/Contract/hooks/useUerContractOtherCostLimitSpanSelectionList';
import { prepareLimitsData } from '../../hooks/prepareLimitsData';
import { getOtherFormulaSourceObjectGql } from '../contractQueries';
import type { TypeItem } from '../OtherCreate/commonData';
import { OTHER_SECTION_OR_CHAPTER, TabsIds, tabsItems, TYPE_VARIANTS } from '../OtherCreate/commonData';
import { useIsUniqueContractOtherCode } from '../OtherCreate/hooks/useIsUniqueContractOtherCode';

import { useContractOtherCostForUpdate } from './hooks/useContractOtherCostForUpdate';
import { useUerUpdateOtherCostCanEdit } from './hooks/useUerUpdateContractOtherCostCanEdit';
import { useUpdateContractOtherCost } from './hooks/useUpdateContractOtherCost';
import { getBimLimitsValues, getUerLimitsValues } from './utils';

const fieldsToCheck = {
    [TabsIds.COMMON]: ['name', 'calculatingTypeValue', 'otherCostType'],
    [TabsIds.PRICE]: ['cost', 'method'],
    [TabsIds.FORMULA]: ['formula'],
};

const title = 'Редактирование прочих затрат';

interface IEditOtherModal {
    onClose: (isSuccess?: boolean) => void;
    contractId: number;
    workOrderId?: number;
    workOrderStatus?: WorkOrderStatusEnum;
    constructionTypeId: number;
    constructionTypes: ConstructionTypeData[];
    otherId?: number;
    otherIdProp?: number;
    workOrderOtherPriceIdProp?: number;
    methodology: ProjectMethodologyEnum;
    isOnlyTypeEditAvailable?: boolean;
    isModify?: boolean;
    isNew?: boolean;
}

const codeKey = 'key';

export const EditOtherModal: React.FC<IEditOtherModal> = (props) => {
    const {
        onClose,
        contractId,
        workOrderId,
        workOrderStatus,
        constructionTypeId,
        constructionTypes,
        otherId,
        otherIdProp,
        methodology,
        isOnlyTypeEditAvailable,
        isModify,
        workOrderOtherPriceIdProp,
        isNew,
    } = props;

    const form = useForm({ shouldUnregister: false });
    const {
        control,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        setValue,
        formState: { isDirty, dirtyFields },
    } = form;
    const currentCode = useWatch({ control, name: 'key' });
    const isDirtyOtherCode = dirtyFields?.key;

    const { otherId: otherIdParam, id: projectId } = useParams();

    const { state: workOrderOtherPriceIdParam }: { state: number } = useLocation();
    const workOrderOtherPriceId = useMemo(
        () => workOrderOtherPriceIdProp ?? workOrderOtherPriceIdParam,
        [workOrderOtherPriceIdParam, workOrderOtherPriceIdProp],
    );

    const currentOtherId = useMemo(
        () => otherIdProp ?? Number(otherIdParam ?? otherId),
        [otherId, otherIdParam, otherIdProp],
    );
    const formRef = useRef<HTMLFormElement>(null);
    const [activeTab, setActiveTab] = useState(tabsItems[0]);
    const [isStateDirty, setIsStateDirty] = useState(false);
    const [selectedType, setSelectedType] = useState<TypeItem>(TYPE_VARIANTS[0]);
    const [selectedChapterOrSection, setSelectedChapterOrSection] = useState(OTHER_SECTION_OR_CHAPTER[0]);
    const [selectedConstructionType, setSelectedConstructionType] = useState<Record<string, boolean>>({
        [constructionTypeId]: true,
    });

    const changeIsStateDirtyHandler = useCallback(() => {
        setIsStateDirty(true);
    }, []);
    const isFormDirty = useMemo(() => isDirty || isStateDirty, [isDirty, isStateDirty]);

    const { projectData, refreshProject } = useProjectData(projectId, !otherIdProp);

    const isUer = useMemo(() => methodology === ProjectMethodologyEnum.Uer, [methodology]);

    const {
        reset: resetOtherForUpdate,
        data: otherForUpdateData,
        status: otherForUpdateStatus,
    } = useContractOtherCostForUpdate({
        contractOtherCostId: currentOtherId,
        contractId,
        methodology,
        constructionTypeId,
    });

    const spanSelectionData = useUerContractOtherCostLimitSpanSelectionList({
        withEffect: isUer,
        contractId,
        otherCostId: currentOtherId,
        constructionTypeId,
    });

    const otherStatus = useMemo(() => otherForUpdateData?.data?.status, [otherForUpdateData?.data?.status]);

    const isUpdateContractCondition = useMemo(() => !!otherIdProp && !isModify, [isModify, otherIdProp]);

    const isCalculationTypeEditable = useMemo(
        () => isUpdateContractCondition || otherStatus === ContractOtherStatusEnum.Draft,
        [isUpdateContractCondition, otherStatus],
    );

    const isNameFieldDisabled = useMemo(
        () =>
            otherForUpdateData?.data?.status === ContractOtherStatusEnum.Draft &&
            workOrderStatus === WorkOrderStatusEnum.InProgress,
        [otherForUpdateData?.data?.status, workOrderStatus],
    );

    const isOtherFromCatalog = useMemo(
        () => otherForUpdateData?.data?.isGlobalOtherCost,
        [otherForUpdateData?.data?.isGlobalOtherCost],
    );

    const isDataFetching = useMemo(() => otherForUpdateStatus === RequestStatus.FETCHING, [otherForUpdateStatus]);

    const {
        updateContractOtherCostMakeRequest,
        updateContractOtherCostReset,
        updateContractOtherCostData,
        updateContractOtherCostStatus,
    } = useUpdateContractOtherCost({
        contractId,
        workOrderId,
        otherId: currentOtherId,
        workOrderStatus,
        selectedType,
        selectedChapterOrSection,
        isCalculationTypeEditable,
        workOrderOtherPriceId,
        isOtherFromCatalog,
        isUpdateContractCondition,
        methodology,
        refreshProject,
        isOnlyTypeEditAvailable,
    });

    const resetData = useCallback(() => {
        resetOtherForUpdate(undefined);
        updateContractOtherCostReset(undefined);
    }, [resetOtherForUpdate, updateContractOtherCostReset]);

    const closeAndResetModal = useCallback(() => {
        resetData();
        onClose?.();
    }, [onClose, resetData]);

    const isUpdating = useMemo(
        () => updateContractOtherCostStatus === RequestStatus.FETCHING,
        [updateContractOtherCostStatus],
    );

    useEffect(() => {
        if (updateContractOtherCostData?.result === true && !isUpdating) {
            resetData();
            onClose?.(true);
        }
    }, [
        isUpdating,
        onClose,
        resetData,
        resetOtherForUpdate,
        updateContractOtherCostData?.result,
        updateContractOtherCostReset,
    ]);

    const areFieldsDisabled = useMemo(
        () =>
            !!workOrderStatus &&
            ![WorkOrderStatusEnum.OnFormation, WorkOrderStatusEnum.InProgress].includes(workOrderStatus),
        [workOrderStatus],
    );

    const onChangeConstructionType = useCallback(
        (id, state) => {
            changeIsStateDirtyHandler();
            setSelectedConstructionType((types) => ({
                ...types,
                [id]: state,
            }));
        },
        [changeIsStateDirtyHandler],
    );

    const { deserializeFromBackend, serializeStringForBackend } = useEditorParseData({});

    useEffect(() => {
        if (otherForUpdateData) {
            const { data } = otherForUpdateData || {};

            const currentType =
                data?.calculatingType === OtherCostCalculationTypeEnum.Calculation
                    ? TYPE_VARIANTS[0]
                    : TYPE_VARIANTS[1];

            const limitsValues = isUer
                ? getUerLimitsValues(data as ContractOtherCostUpdateData, spanSelectionData.data)
                : getBimLimitsValues(data as BimContractOtherCostData);

            setSelectedConstructionType((prevState) => {
                const currentLimitCheckboxValues = Object.keys(limitsValues.cost).reduce(
                    (acc: Record<string, boolean>, key) => {
                        acc[key] = true;

                        return acc;
                    },
                    {},
                );

                if (isUer && data?.status === ContractOtherStatusEnum.Draft) {
                    return {
                        ...prevState,
                        ...currentLimitCheckboxValues,
                    };
                }

                return currentLimitCheckboxValues;
            });

            const calculatingTypeValue = data?.calculatingTypeValue;
            const otherCode = data && 'key' in data && data?.key;
            const chapter = data?.chapter;
            const formula = data && 'formula' in data ? deserializeFromBackend(data?.formula) : '';
            const catalogOtherCostType =
                data && 'catalogOtherCostType' in data ? data?.catalogOtherCostType : undefined;

            setSelectedType(currentType);
            setSelectedChapterOrSection((chapter ? OTHER_SECTION_OR_CHAPTER[0] : OTHER_SECTION_OR_CHAPTER[1]) as any);

            reset({
                name: otherForUpdateData?.data?.name,
                ...limitsValues,
                ...(isNotNil(calculatingTypeValue) && { calculatingTypeValue }),
                ...(isNotNil(calculatingTypeValue) && { calculatingTypeValue }),
                ...(isNotNil(otherCode) && { key: otherCode }),
                formula,
                otherCostType: catalogOtherCostType,
            });
        }
    }, [deserializeFromBackend, isUer, onChangeConstructionType, otherForUpdateData, reset, spanSelectionData.data]);

    const onTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    const onChangeType = useCallback(
        (newType) => {
            changeIsStateDirtyHandler();
            setSelectedType(newType);
        },
        [changeIsStateDirtyHandler],
    );

    const changeSelectedChapterOrSection = useCallback(
        (value) => {
            changeIsStateDirtyHandler();
            setSelectedChapterOrSection(value);
        },
        [changeIsStateDirtyHandler],
    );

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: closeAndResetModal,
        });

    const onCancel = useCallback(() => {
        if (isFormDirty) {
            handleOpenConfirmationDialog();
        } else {
            closeAndResetModal();
        }
    }, [closeAndResetModal, handleOpenConfirmationDialog, isFormDirty]);

    const onSubmit = useCallback(
        (data) => {
            if (isOnlyTypeEditAvailable) {
                data = {
                    ...data,
                    name: otherForUpdateData?.data?.name,
                    type: selectedType,
                    calculatingTypeValue: otherForUpdateData?.data?.calculatingTypeValue,
                    selectedChapterOrSection,
                };
            }

            if (otherForUpdateData?.data?.isGlobalOtherCost) {
                data = { ...data, name: otherForUpdateData?.data?.name };
            }

            const serializedFormula = serializeStringForBackend(data.formula);

            if (!data.name) {
                setActiveTab(tabsItems[0]);
                setTimeout(() => {
                    setError('name', { message: 'Обязательное поле' });
                }, 0);

                return;
            }

            if (replacerCost(data.calculatingTypeValue) < 0) {
                setActiveTab(tabsItems[0]);
                setTimeout(() => {
                    setError('calculatingTypeValue', { message: 'Процент должен быть не отрицательным числом' });
                }, 0);

                return;
            }

            const { limitsArray, haveError, errorMsgObj } = prepareLimitsData(selectedConstructionType, data, isUer);

            if (haveError) {
                setActiveTab(tabsItems[1]);

                if (errorMsgObj) {
                    Object.entries(errorMsgObj).forEach(([id, errorMessage]) => {
                        setError(`method.${id}`, { message: errorMessage });
                    });
                }

                return;
            }

            updateContractOtherCostMakeRequest(data, limitsArray, serializedFormula);
        },
        [
            isOnlyTypeEditAvailable,
            isUer,
            otherForUpdateData?.data?.calculatingTypeValue,
            otherForUpdateData?.data?.isGlobalOtherCost,
            otherForUpdateData?.data?.name,
            selectedChapterOrSection,
            selectedConstructionType,
            selectedType,
            serializeStringForBackend,
            setError,
            updateContractOtherCostMakeRequest,
        ],
    );

    const formulaFieldDisabled = selectedType.id !== OtherCostCalculationTypeEnum.Percent;

    const costRounding = useMemo(
        () => projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0,
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding],
    );

    const { isGlobalOtherCost, name, chapter, calculatingTypeValue } = useMemo(
        () => (otherForUpdateData?.data as ContractOtherCostUpdateData) ?? {},
        [otherForUpdateData?.data],
    );
    const canEditConfig = useUerUpdateOtherCostCanEdit({
        data: otherForUpdateData?.data as ContractOtherCostUpdateData,
        contractStatus: projectData?.contractStatus,
    });

    const { isContractOtherCodeUnique } = useIsUniqueContractOtherCode({
        code: currentCode,
        contractOtherCostId: otherForUpdateData?.data?.id,
        isDirtyCode: isDirtyOtherCode,
        contractId,
    });

    useEffect(() => {
        if (isDirtyOtherCode && currentCode && !isContractOtherCodeUnique) {
            setError?.(codeKey, { message: 'Прочая с таким кодом уже существует' });
        } else {
            clearErrors?.(codeKey);
        }
    }, [clearErrors, currentCode, isContractOtherCodeUnique, isDirtyOtherCode, setError]);

    return (
        <ModalDialog
            title={title}
            isOpen
            hasOverlay
            withTitleCloseButton
            onClose={onCancel}
            onConfirm={handleSubmit(onSubmit)}
            isCommit={isUpdating}
            btnConfirmDisabled={!isFormDirty || !isContractOtherCodeUnique}
            isOnClickOutsideDisable
        >
            <FormProvider {...form}>
                {/* TODO: временная копия для УЕР со своими правилами чтобы не конфликтовало с БИМ + старое отображение */}
                {isNew && isUer ? (
                    <EditOtherFormNew
                        activeTab={activeTab}
                        handleTabChange={onTabChange}
                        tabItems={tabsItems}
                        control={control}
                        fieldsToCheck={fieldsToCheck}
                        isDataFetching={isDataFetching}
                        selectedType={selectedType}
                        handleChangeType={onChangeType}
                        changeSelectedChapterOrSection={changeSelectedChapterOrSection}
                        selectedChapterOrSection={selectedChapterOrSection}
                        currentConstructionTypeId={constructionTypeId}
                        selectedConstructionType={selectedConstructionType}
                        handleChangeConstructionType={onChangeConstructionType}
                        constructionTypes={constructionTypes}
                        ref={formRef}
                        methodology={methodology}
                        getObjectSourceQuery={getOtherFormulaSourceObjectGql}
                        costRounding={costRounding}
                        clearErrors={clearErrors}
                        setValue={setValue}
                        spanSelectionData={spanSelectionData}
                        otherId={currentOtherId}
                        chapter={chapter}
                        isGlobalOtherCost={isGlobalOtherCost}
                        name={name}
                        calculatingTypeValue={calculatingTypeValue}
                        canEditConfig={canEditConfig}
                    />
                ) : (
                    <EditOtherForm
                        activeTab={activeTab}
                        handleTabChange={onTabChange}
                        tabItems={tabsItems}
                        control={control}
                        fieldsToCheck={fieldsToCheck}
                        isDataFetching={isDataFetching}
                        chapter={otherForUpdateData?.data?.chapter}
                        isGlobal={otherForUpdateData?.data?.isGlobalOtherCost}
                        otherStatus={otherStatus}
                        globalOtherName={otherForUpdateData?.data?.name}
                        selectedType={selectedType}
                        handleChangeType={onChangeType}
                        areFieldsDisabled={areFieldsDisabled}
                        isChapterEditable={isUpdateContractCondition}
                        isCalculationTypeEditable={isCalculationTypeEditable}
                        isNameFieldDisabled={isNameFieldDisabled}
                        isOnlyTypeEditAvailable={isOnlyTypeEditAvailable}
                        changeSelectedChapterOrSection={changeSelectedChapterOrSection}
                        selectedChapterOrSection={selectedChapterOrSection}
                        currentConstructionTypeId={constructionTypeId}
                        selectedConstructionType={selectedConstructionType}
                        handleChangeConstructionType={onChangeConstructionType}
                        constructionTypes={constructionTypes}
                        isModalReadOnly={false}
                        ref={formRef}
                        methodology={methodology}
                        isFormulaFieldDisabled={formulaFieldDisabled}
                        getObjectSourceQuery={getOtherFormulaSourceObjectGql}
                        calculatingTypeValue={otherForUpdateData?.data?.calculatingTypeValue}
                        costRounding={costRounding}
                        clearErrors={clearErrors}
                        setValue={setValue}
                        noLimitSpan={!!otherIdProp}
                        spanSelectionData={spanSelectionData}
                        isModify={isModify}
                        otherId={currentOtherId}
                    />
                )}
            </FormProvider>
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ModalDialog>
    );
};

EditOtherModal.displayName = 'EditOtherModal';
